import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { useAlleNachrichten } from '../../hooks/buch'
import { useHighlights, useMembers, useTracks, useTrip } from '../../hooks/queries'
import { ausschnittAus, bildStil } from '../../lib/ausschnitt'
import { Buchkarte } from './Buchkarte'
import { Beitrag, Punktseite } from './Buchteile'
import { Druckknopf } from './Druckknopf'
import { dayLabel, tripPeriod } from '../../lib/format'
import { datumText } from '../../lib/ortszeit'
import { BackIcon } from '../../components/Icons'
import '../../styles/buch.css'

/**
 * Die Reise als Buch (#14).
 *
 * **Antworten stehen nicht darin, und das entscheidet nicht diese Seite.**
 * `Visibility::scope()` gibt dem Absender ohne `?with=` ausschließlich
 * `kind = 'broadcast'` zurück — hier wird deshalb nichts gefiltert. Die eine
 * Regel bleibt an der einen Stelle; ein Filter hier wäre eine zweite, und die
 * zweite läuft irgendwann auseinander.
 *
 * **Das PDF macht der Browser.** `window.print()` auf dieser Seite, die Regeln
 * dafür stehen in `styles/buch.css`. Eine PDF-Bibliothek müsste Fließtext,
 * Bilder und Umbrüche von Hand setzen — viel Code für ein Ergebnis, das
 * schlechter aussähe. Der Preis ist der Druckdialog des Systems; deshalb sagt
 * der Knopf, was er tut, statt eine Datei zu versprechen.
 */
export function BuchPage() {
  const { tripId } = useParams()
  const id = Number(tripId)
  const [suche] = useSearchParams()

  const trip = useTrip(id)
  const punkte = useHighlights(id, true)
  const spuren = useTracks(id, true)
  const empfaenger = useMembers(id, true)
  const stand = useAlleNachrichten(id)

  const [karteFertig, setKarteFertig] = useState(false)

  const versatz = trip.data?.tz_offset_min ?? null
  const von = suche.get('von')
  const bis = suche.get('bis')

  /**
   * Der Zeitraum schneidet zu — und er schneidet **alles** zu, nicht nur die
   * Beiträge. Ein Buch, in dem die Karte die ganze Reise zeigt und der Text
   * eine Woche, wäre zwei Bücher in einem.
   */
  const imZeitraum = useMemo(() => {
    const ab = von !== null && von !== '' ? new Date(`${von}T00:00:00Z`).getTime() : null
    const zu = bis !== null && bis !== '' ? new Date(`${bis}T23:59:59Z`).getTime() : null
    return (iso: string | null): boolean => {
      if (iso === null) return ab === null && zu === null ? true : false
      const wann = new Date(iso).getTime()
      if (ab !== null && wann < ab) return false
      if (zu !== null && wann > zu) return false
      return true
    }
  }, [von, bis])

  const beitraege = stand.nachrichten.filter((m) => imZeitraum(m.created_at))
  const gezeigtePunkte = (punkte.data?.highlights ?? [])
    .filter((p) => p.published_at !== null)
    .filter((p) => imZeitraum(p.happened_at))

  // Der Titel des Fensters wird im Druckdialog zum Dateinamen -- deshalb steht
  // dort der Name der Reise und nicht „Travel Broadcast".
  useEffect(() => {
    const alt = document.title
    if (trip.data !== undefined) document.title = trip.data.title
    return () => {
      document.title = alt
    }
  }, [trip.data])

  const bereit = !stand.laeuft && (karteFertig || gezeigtePunkte.length + (spuren.data?.tracks?.length ?? 0) === 0)

  return (
    <div className="buch">
      <div className="buch__leiste buch__nurbildschirm">
        <Link className="icon-button" to={`/t/${id}/info`} aria-label="Zurück zur Reise">
          <BackIcon />
        </Link>
        <span className="bar__grow">
          {stand.laeuft
            ? `Wird zusammengestellt … (${stand.seiten} Seiten)`
            : `${beitraege.length} Beiträge, ${gezeigtePunkte.length} Punkte`}
        </span>
        {stand.fehler !== null && (
          <p className="notice" role="alert" style={{ margin: 0 }}>
            {stand.fehler}
          </p>
        )}
        <Druckknopf
          bereit={bereit && stand.fehler === null}
          wartetext={stand.laeuft ? 'Noch nicht fertig …' : 'Nicht bereit'}
          drucktext="Buch drucken oder als PDF sichern"
        />
      </div>

      <section className="buch__deckel">
        {trip.data?.cover_url != null && (
          <span className="buch__deckelbild">
            <img
              src={trip.data.cover_url}
              alt=""
              style={bildStil(ausschnittAus(trip.data.cover_crop))}
            />
          </span>
        )}
        <h1>{trip.data?.title ?? 'Reise'}</h1>
        <p>{tripPeriod(trip.data?.starts_on ?? null, trip.data?.ends_on ?? null)}</p>
        <p>{trip.data?.owner.display_name}</p>
      </section>

      {(gezeigtePunkte.length > 0 || (spuren.data?.tracks?.length ?? 0) > 0) && (
        <section className="buch__abschnitt">
          <h2>Die Route</h2>
          <Buchkarte
            tracks={spuren.data?.tracks ?? []}
            punkte={gezeigtePunkte}
            onFertig={() => setKarteFertig(true)}
          />
        </section>
      )}

      <section className="buch__abschnitt">
        <h2>Der Bericht</h2>
        {beitraege.length === 0 && <p>In diesem Zeitraum steht kein Beitrag.</p>}
        {beitraege.map((nachricht, i) => {
          const vorher = beitraege[i - 1]
          const neuerTag =
            vorher === undefined || dayLabel(vorher.created_at) !== dayLabel(nachricht.created_at)
          return (
            <div key={nachricht.id}>
              {neuerTag && <p className="buch__tag">{dayLabel(nachricht.created_at)}</p>}
              <Beitrag nachricht={nachricht} versatz={versatz} />
            </div>
          )
        })}
      </section>

      {gezeigtePunkte.length > 0 && (
        <section className="buch__abschnitt">
          <h2>Die Punkte auf der Karte</h2>
          {gezeigtePunkte.map((punkt, i) => (
            <Punktseite key={punkt.id} punkt={punkt} nummer={i + 1} reiseVersatz={versatz} />
          ))}
        </section>
      )}

      <section className="buch__abschnitt">
        {/* **Der Stand des Exporttags, und die Seite sagt es.** Wer wann dabei
            war, ließe sich aus `join_requests` rekonstruieren -- das wäre ein
            eigenes Vorhaben und für ein Buch die falsche Genauigkeit. */}
        <h2>Empfänger</h2>
        <p className="buch__stand">Stand: {datumText(new Date().toISOString(), versatz)}</p>
        <ul className="buch__liste">
          {(empfaenger.data ?? [])
            .filter((m) => m.role === 'recipient' && m.left !== true)
            .map((m) => (
              <li key={m.user.id}>{m.user.display_name}</li>
            ))}
        </ul>
      </section>
    </div>
  )
}
